
// Utility function to remove accents from text (useful for Greek characters)
export const removeAccents = (text: string): string => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/[άάὰἀἁἂἃἄἅἆἇᾰᾱᾶᾷᾳᾴᾲ]/g, 'α')
    .replace(/[έέὲἐἑἒἓἔἕ]/g, 'ε')
    .replace(/[ήήὴἠἡἢἣἤἥἦἧῆῇῃῄῂ]/g, 'η')
    .replace(/[ίίὶἰἱἲἳἴἵἶἷῖῗ]/g, 'ι')
    .replace(/[όόὸὀὁὂὃὄὅ]/g, 'ο')
    .replace(/[ύύὺὐὑὒὓὔὕὖὗῦῧ]/g, 'υ')
    .replace(/[ώώὼὠὡὢὣὤὥὦὧῶῷῳῴῲ]/g, 'ω')
    .replace(/[ΆΆᾺἈἉἊἋἌἍἎἏᾸᾹᾶᾷᾼᾴᾲ]/g, 'Α')
    .replace(/[ΈΈῈἘἙἚἛἜἝ]/g, 'Ε')
    .replace(/[ΉΉῊἨἩἪἫἬἭἮἯῌῄῂ]/g, 'Η')
    .replace(/[ΊΊῚἸἹἺἻἼἽἾἿῘῙῖῗ]/g, 'Ι')
    .replace(/[ΌΌῸὈὉὊὋὌὍ]/g, 'Ο')
    .replace(/[ΎΎῪὙὛὝὟῨῩῦῧ]/g, 'Υ')
    .replace(/[ΏΏῺὨὩὪὫὬὭὮὯῼῴῲ]/g, 'Ω');
};

// Transliterate Greek to Latin characters
export const greekToLatin = (text: string): string => {
  const greekToLatinMap: { [key: string]: string } = {
    'α': 'a', 'Α': 'A',
    'β': 'b', 'Β': 'B',
    'γ': 'g', 'Γ': 'G',
    'δ': 'd', 'Δ': 'D',
    'ε': 'e', 'Ε': 'E',
    'ζ': 'z', 'Ζ': 'Z',
    'η': 'i', 'Η': 'I',
    'θ': 'th', 'Θ': 'TH',
    'ι': 'i', 'Ι': 'I',
    'κ': 'k', 'Κ': 'K',
    'λ': 'l', 'Λ': 'L',
    'μ': 'm', 'Μ': 'M',
    'ν': 'n', 'Ν': 'N',
    'ξ': 'x', 'Ξ': 'X',
    'ο': 'o', 'Ο': 'O',
    'π': 'p', 'Π': 'P',
    'ρ': 'r', 'Ρ': 'R',
    'σ': 's', 'Σ': 'S',
    'ς': 's',
    'τ': 't', 'Τ': 'T',
    'υ': 'y', 'Υ': 'Y',
    'φ': 'f', 'Φ': 'F',
    'χ': 'ch', 'Χ': 'CH',
    'ψ': 'ps', 'Ψ': 'PS',
    'ω': 'o', 'Ω': 'O'
  };

  return text.replace(/[α-ωΑ-Ωάέήίόύώ]/g, (char) => greekToLatinMap[char] || char);
};

// Transliterate Latin to Greek characters (basic phonetic mapping)
export const latinToGreek = (text: string): string => {
  const latinToGreekMap: { [key: string]: string } = {
    'a': 'α', 'A': 'Α',
    'b': 'β', 'B': 'Β',
    'g': 'γ', 'G': 'Γ',
    'd': 'δ', 'D': 'Δ',
    'e': 'ε', 'E': 'Ε',
    'z': 'ζ', 'Z': 'Ζ',
    'i': 'ι', 'I': 'Ι',
    'k': 'κ', 'K': 'Κ',
    'l': 'λ', 'L': 'Λ',
    'm': 'μ', 'M': 'Μ',
    'n': 'ν', 'N': 'Ν',
    'x': 'ξ', 'X': 'Ξ',
    'o': 'ο', 'O': 'Ο',
    'p': 'π', 'P': 'Π',
    'r': 'ρ', 'R': 'Ρ',
    's': 'σ', 'S': 'Σ',
    't': 'τ', 'T': 'Τ',
    'y': 'υ', 'Y': 'Υ',
    'f': 'φ', 'F': 'Φ'
  };

  return text.replace(/[a-zA-Z]/g, (char) => latinToGreekMap[char] || char);
};

// Calculate Levenshtein distance for fuzzy matching
export const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }

  return matrix[str2.length][str1.length];
};

// Calculate similarity score (0-1, where 1 is exact match)
export const calculateSimilarity = (str1: string, str2: string): number => {
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 1;
  
  const distance = levenshteinDistance(str1, str2);
  return 1 - distance / maxLength;
};

// Normalize text for search (lowercase + remove accents + transliterations)
export const normalizeForSearch = (text: string): string => {
  const cleanText = removeAccents(text.toLowerCase().trim());
  return cleanText;
};

// Create multiple search variants for cross-script matching
export const createSearchVariants = (text: string): string[] => {
  const normalized = normalizeForSearch(text);
  const variants = [normalized];
  
  // Add Greek to Latin transliteration
  const latinVariant = greekToLatin(normalized);
  if (latinVariant !== normalized) {
    variants.push(latinVariant);
  }
  
  // Add Latin to Greek transliteration
  const greekVariant = latinToGreek(normalized);
  if (greekVariant !== normalized) {
    variants.push(greekVariant);
  }
  
  return [...new Set(variants)]; // Remove duplicates
};

// Check if search term matches in text with fuzzy logic and cross-script support
export const fuzzyMatch = (searchTerm: string, text: string, threshold: number = 0.6): boolean => {
  const searchVariants = createSearchVariants(searchTerm);
  const textVariants = createSearchVariants(text);
  
  // Check all combinations of search variants against text variants
  for (const searchVariant of searchVariants) {
    for (const textVariant of textVariants) {
      // Exact substring match
      if (textVariant.includes(searchVariant)) {
        return true;
      }
      
      // Split text into words and check each word
      const words = textVariant.split(/\s+/);
      for (const word of words) {
        if (word.includes(searchVariant)) {
          return true;
        }
        
        // Fuzzy match for individual words
        const similarity = calculateSimilarity(searchVariant, word);
        if (similarity >= threshold) {
          return true;
        }
      }
      
      // Fuzzy match against entire text
      const overallSimilarity = calculateSimilarity(searchVariant, textVariant);
      if (overallSimilarity >= threshold) {
        return true;
      }
    }
  }
  
  return false;
};
